export interface PoliticaMailAccion {
  nombre?: string;
  descripcion?: string;
  estado?: string;
}

export interface PoliticaMail {
  requeridoParaTerminar?: boolean;
  template?: string;
  acciones?: PoliticaMailAccion[];
}

export interface PoliticaMailForm {
  template: string;
  requeridoParaTerminar: string;
  acciones: PoliticaMailAccion[];
}

export const POLITICA_MAIL_PLACEHOLDERS = [
  '{{asunto}}',
  '{{from}}',
  '{{to}}',
  '{{usuario}}',
  '{{usuarioEmail}}',
  '{{empresa}}',
  '{{rfc}}',
] as const;

export function defaultPoliticaMailForm(): PoliticaMailForm {
  return {
    template: '',
    requeridoParaTerminar: 'false',
    acciones: [],
  };
}

export function hasValidMailTemplate(template?: string | null): boolean {
  return Boolean(template?.trim());
}

export function dtoToPoliticaMailForm(dto?: PoliticaMail | null): PoliticaMailForm {
  if (!dto) return defaultPoliticaMailForm();

  return {
    template: dto.template ?? '',
    requeridoParaTerminar: String(dto.requeridoParaTerminar ?? false),
    acciones: (dto.acciones ?? []).map(accion => ({
      nombre: accion.nombre ?? '',
      descripcion: accion.descripcion ?? '',
      estado: accion.estado ?? '',
    })),
  };
}

export function emptyPoliticaMailDto(): PoliticaMail {
  return {
    requeridoParaTerminar: false,
    template: '',
    acciones: [],
  };
}

export function politicaMailFormToDto(form: PoliticaMailForm): PoliticaMail {
  const template = form.template.trim();
  const requeridoParaTerminar = form.requeridoParaTerminar === 'true';

  const acciones = form.acciones
    .map(accion => ({
      nombre: accion.nombre?.trim() ?? '',
      descripcion: accion.descripcion?.trim() ?? '',
      estado: accion.estado?.trim() ?? '',
    }))
    .filter(accion => accion.nombre || accion.descripcion || accion.estado);

  if (!template && !requeridoParaTerminar && !acciones.length) {
    return emptyPoliticaMailDto();
  }

  return {
    template,
    requeridoParaTerminar,
    acciones,
  };
}

export function validatePoliticaMailForm(form: PoliticaMailForm): Partial<Record<keyof PoliticaMailForm, string>> {
  const errs: Partial<Record<keyof PoliticaMailForm, string>> = {};

  if (form.requeridoParaTerminar === 'true' && !hasValidMailTemplate(form.template)) {
    errs.template = 'Se requiere plantilla con texto para exigir el mail al terminar la actividad';
  }

  const hasIncompleteAccion = form.acciones.some(accion => {
    const hasAny = Boolean(accion.nombre?.trim() || accion.descripcion?.trim() || accion.estado?.trim());
    const hasAll = Boolean(accion.nombre?.trim() && accion.descripcion?.trim() && accion.estado?.trim());
    return hasAny && !hasAll;
  });

  if (hasIncompleteAccion) {
    errs.acciones = 'Cada acción debe incluir nombre, descripción y estado';
  }

  return errs;
}

export function isPoliticaMailConfigured(dto?: PoliticaMail | null): boolean {
  if (!dto) return false;
  return hasValidMailTemplate(dto.template)
    || Boolean(dto.requeridoParaTerminar)
    || Boolean(dto.acciones?.length);
}
